import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { showToast } from '../Components/utils/ShowToast';
import { LoginContext } from './LoginContext';

export const BookmarkContext = createContext();

export const BookmarkProvider = ({ children }) => {
  const { isLogin } = useContext(LoginContext);
  const [bookmarkedSellers, setBookmarkedSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (isLogin) {
      fetchBookmarkedSellers();
    } else {
      setBookmarkedSellers([]);
      setLoading(false);
    }
  }, [isLogin]);

  const fetchBookmarkedSellers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/bookmarks`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setBookmarkedSellers(response.data.bookmarkedSellers);
    } catch (error) {
      console.error('Failed to fetch bookmarked sellers:', error);
    }
    finally{
      setLoading(false);
    }
  };

  const addBookmark = async (seller) => {
    if (!isLogin) {
      showToast('Please login to bookmark sellers', 'warning');
      return;
    }
    const sellerId = seller.id || seller._id;
    setActionLoading((prev) => ({ ...prev, [sellerId]: true }));
    setBookmarkedSellers((prev) => [...prev, seller]); // Optimistic update
    try {
      await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/bookmarks`,
        { sellerId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      showToast('Seller added to bookmarks', 'success');
    } catch (error) {
      console.error('Failed to add bookmark:', error);
      setBookmarkedSellers((prev) => prev.filter((s) => (s.id || s._id) !== sellerId)); // Rollback
      showToast(error.response?.data?.message || 'Failed to add bookmark', 'error');
    } finally {
      setActionLoading((prev) => ({ ...prev, [sellerId]: false }));
    }
  };

  const removeBookmark = async (sellerId) => {
    if (!isLogin) {
      showToast('Please login to remove bookmarks', 'warning');
      return;
    }
    setActionLoading((prev) => ({ ...prev, [sellerId]: true }));
    const originalSellers = [...bookmarkedSellers];
    setBookmarkedSellers((prev) => prev.filter((s) => (s.id || s._id) !== sellerId)); // Optimistic update
    try {
      await axios.delete(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/bookmarks/${sellerId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      showToast('Seller removed from bookmarks', 'success');
    } catch (error) {
      console.error('Failed to remove bookmark:', error);
      setBookmarkedSellers(originalSellers); // Rollback
      showToast('Failed to remove bookmark', 'error');
    } finally {
      setActionLoading((prev) => ({ ...prev, [sellerId]: false }));
    }
  };

  const isBookmarked = (sellerId) => {
    return bookmarkedSellers.some((seller) => (seller.id || seller._id) === sellerId);
  };

  return (
    <BookmarkContext.Provider
      value={{ bookmarkedSellers, addBookmark, removeBookmark, isBookmarked, loading, actionLoading, fetchBookmarkedSellers }}
    >
      {children}
    </BookmarkContext.Provider>
  );
};

export const useBookmarkContext = () => {
  const context = useContext(BookmarkContext);
  if (!context) throw new Error('useBookmarkContext must be used within a BookmarkProvider');
  return context;
};